import {
  ContactRequest,
  useBannerStore,
  useCookieStore,
} from '@packages/shared';
import { useMutation } from '@tanstack/react-query';
import { createTsForm } from '@ts-react/form';
import formMapping from '@utils/formMapping';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'next-i18next';
import { ReactNode } from 'react';
import * as z from 'zod';

const FormComponent = ({
  children,
  onSubmit,
}: {
  children: ReactNode;
  onSubmit: () => void;
}) => {
  const { t } = useTranslation('common');
  const closeBanner = useBannerStore((state) => state.closeBanner);
  return (
    <form onSubmit={onSubmit} className="flex w-full flex-col gap-2 text-left">
      {children}
      <div className="flex justify-end gap-2">
        <button
          className="ring-primary inline-flex items-center self-end rounded-full border bg-white px-6 py-3 text-xs font-medium shadow-sm outline-none transition-all focus:ring-2 focus:ring-offset-2"
          onClick={() => closeBanner()}
          type="button"
          data-testid="feedback-banner-close"
        >
          {t('banner.form.form_close_button')}
        </button>

        <button
          className="ring-primary inline-flex items-center self-end rounded-full border bg-black px-6 py-3 text-xs font-medium text-white shadow-sm outline-none transition-all focus:ring-2 focus:ring-offset-2"
          type="submit"
          data-testid="feedback-banner-submit"
        >
          {t('banner.form.form_submit_button')}
        </button>
      </div>
    </form>
  );
};
// A typesafe React component
const FeedbackForm = createTsForm(formMapping, { FormComponent });
const FeedbackSchema = z.object({
  message: z.string(),
});

export default function FeedbackFormComp() {
  const feedbackMutation = useMutation<
    AxiosResponse,
    AxiosError,
    ContactRequest['body']
  >({
    mutationFn: (feedback) => {
      return axios.post('/api/contact', feedback);
    },
  });
  const { t } = useTranslation();

  const { setFormState, setLastAnswered } = useBannerStore((state) => ({
    setFormState: state.setFormState,
    setLastAnswered: state.setLastAnswered,
  }));

  const posthogId = useCookieStore((state) => state.posthogId);

  function handleSubmit(data: { message: string }) {
    feedbackMutation.mutate({ ...data, type: 'feedback', posthogId });
    setFormState('answered');
    setLastAnswered();
  }

  return (
    <FeedbackForm
      schema={FeedbackSchema}
      onSubmit={handleSubmit}
      defaultValues={{
        message: '',
      }}
      props={{
        message: {
          type: 'textarea',
          label: t('banner.form.feedback_label'),
          placeholder: t('banner.form.feedback_placeholder'),
          min: 1,
          max: 500,
          autoFocus: true,
        },
      }}
    />
  );
}
